import { Head, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { employeePath, employeesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

import EmployeeForm from "./_form"

type PermissionCategories = Record<string, string[]>

interface EmployeeData {
  id: number
  first_name: string
  last_name: string
  initials: string
  formal_name: string
  report_display_name: string
  job_title: string
  email: string
  username: string
  work_phone: string
  cell_phone: string
  home_phone: string
  status: string
  employment_type: number | null
  worker_type: number
  org_level: number | null
  pay_rate: number | null
  pay_type: number
  mileage_rate: number | null
  pto_days: number | null
  rdo_days: number | null
  sick_days: number | null
  specialty: string
  quickbooks_name: string
  quickbooks_vendor: string
  sig_profile: string
  sig_vcard: string
  sig_show_cell: boolean
  permissions: Record<string, boolean>
  roles: string[]
}

interface Props {
  employee: EmployeeData
  permission_categories: PermissionCategories
  role_options: string[]
  is_admin: boolean
  errors?: Record<string, string[]>
}

export default function Edit({
  employee,
  permission_categories,
  role_options,
  is_admin,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Employees", href: employeesPath() },
    { title: "Edit", href: `/employees/${employee.id}/edit` },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    first_name: employee.first_name || "",
    last_name: employee.last_name || "",
    initials: employee.initials || "",
    formal_name: employee.formal_name || "",
    report_display_name: employee.report_display_name || "",
    job_title: employee.job_title || "",
    email: employee.email || "",
    username: employee.username || "",
    work_phone: employee.work_phone || "",
    cell_phone: employee.cell_phone || "",
    home_phone: employee.home_phone || "",
    status: employee.status || "active",
    employment_type: employee.employment_type,
    worker_type: employee.worker_type ?? 0,
    org_level: employee.org_level,
    pay_rate: employee.pay_rate,
    pay_type: employee.pay_type ?? 0,
    mileage_rate: employee.mileage_rate,
    pto_days: employee.pto_days,
    rdo_days: employee.rdo_days,
    sick_days: employee.sick_days,
    specialty: employee.specialty || "",
    quickbooks_name: employee.quickbooks_name || "",
    quickbooks_vendor: employee.quickbooks_vendor || "",
    sig_profile: employee.sig_profile || "",
    sig_vcard: employee.sig_vcard || "",
    sig_show_cell: employee.sig_show_cell ?? false,
    permissions: employee.permissions || {},
    roles: employee.roles || [],
  })

  transform((data) => ({ employee: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(employeePath(employee.id))
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Employee" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Edit Employee</h1>

        <EmployeeForm
          data={data}
          setData={(key, value) =>
            setData(key as keyof typeof data, value as never)
          }
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Update Employee"
          permission_categories={permission_categories}
          role_options={role_options}
          isAdmin={is_admin}
        />
      </div>
    </AppLayout>
  )
}
